import 'temporal-polyfill/global'

export function getTemporalNow() {
  return Temporal.Now.zonedDateTimeISO()
}

/**
 * Convert a Date object to a Temporal.ZonedDateTime object
 * @param date
 * @param timeZone Optional Timezone, defaults to environment's current Timezone
 * @returns
 */
export function dateToZonedDatetime(date: Date, timeZone: string = Temporal.Now.timeZoneId()) {
  return Temporal.ZonedDateTime.from({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
    millisecond: date.getMilliseconds(),
    timeZone,
  })
}

export function formatDate(date: Date) {
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date)
}

export function formatDateShort(date: Date) {
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date)
}

export function formatDateLong(date: Date) {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

/**
 * Format a date Month Day format (Jan 02)
 * @param date
 */
export function formatDateMonthDay(date: Date) {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date)
}

export function formatDateAPI(date: Date) {
  const temporal = dateToZonedDatetime(date).toPlainDate()

  return temporal.toString()
}

export function getDateDiff(startDate: Date, endDate: Date) {
  const start = dateToZonedDatetime(startDate)
  const end = dateToZonedDatetime(endDate)

  return end.since(start, { largestUnit: 'day' })?.days
}

export function getMaxDate(dates: Date[]) {
  return new Date(Math.max(...dates.map(date => date.getTime())))
}

export function isToday(datetime: Temporal.ZonedDateTime) {
  const today = Temporal.Now.zonedDateTimeISO()
  return datetime.startOfDay().equals(today.startOfDay())
}

export function zonedDatetimeToDate(temporal: Temporal.ZonedDateTime) {
  return instantToDate(temporal.toInstant())
}

export function instantToDate(instant: Temporal.Instant) {
  return new Date(instant.epochMilliseconds)
}

/** startOf* Helper functions  */
export function startOfDay(datetime: Temporal.ZonedDateTime = Temporal.Now.zonedDateTimeISO()) {
  return datetime.startOfDay()
}

export function startOfTomorrow() {
  return Temporal.Now.zonedDateTimeISO().add({ days: 1 }).startOfDay()
}

export function startOfYesterday() {
  return Temporal.Now.zonedDateTimeISO().subtract({ days: 1 }).startOfDay()
}

export function startOfMonth(datetime: Temporal.ZonedDateTime) {
  return datetime.with({ day: 1 }).startOfDay()
}

export function startOfQuarter(datetime: Temporal.ZonedDateTime) {
  const month = datetime.month
  const quarterStartMonth = Math.floor((month - 1) / 3) * 3 + 1 // eslint-disable-line no-mixed-operators
  return datetime.with({ month: quarterStartMonth, day: 1 }).startOfDay()
}

export function startOfYear(datetime: Temporal.ZonedDateTime) {
  return datetime.with({ month: 1, day: 1 }).startOfDay()
}

export function getCurrentYear() {
  return Temporal.Now.zonedDateTimeISO().year
}
